import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H } from '../types';
import { saveSlot, loadSlot, deleteSlot, loadAutosave, clearAutosave, relativeTime } from '../systems/SaveManager';
import { GameSceneData } from './GameScene';
import { getWorldTheme } from '../config/levels';

export interface PauseSceneData {
  snapshot: GameSceneData;
}

export class PauseScene extends Phaser.Scene {
  private snapshot!: GameSceneData;
  private pendingSlot: (1 | 2 | 3) | null = null;
  private confirmGroup: Phaser.GameObjects.GameObject[] = [];

  constructor() { super({ key: 'PauseScene' }); }

  init(data: PauseSceneData) {
    this.snapshot = data.snapshot;
  }

  create() {
    this.add.rectangle(CANVAS_W / 2, CANVAS_H / 2, CANVAS_W, CANVAS_H, 0x131120, 0.85).setOrigin(0.5);

    this.add.text(CANVAS_W / 2, 72, 'PAUZĂ', {
      fontFamily: 'monospace', fontSize: '48px', color: '#ffd166',
    }).setOrigin(0.5);

    this.add.text(CANVAS_W / 2, 120,
      `${getWorldTheme(this.snapshot.world)} — Lume ${this.snapshot.world}-${this.snapshot.stage}`,
      { fontFamily: 'monospace', fontSize: '16px', color: '#06d6a0' }
    ).setOrigin(0.5);

    const resumeBtn = this.makeBtn(CANVAS_W / 2, 188, '▶ CONTINUĂ (ESC)', () => this.resume());

    // Autosave row
    const autosaveBlob = loadAutosave();
    if (autosaveBlob) {
      this.add.text(CANVAS_W / 2 - 60, 232,
        `AUTOSAVE — ${getWorldTheme(autosaveBlob.data.world)} (${autosaveBlob.data.world}-${autosaveBlob.data.stage}) · ${relativeTime(autosaveBlob.savedAt)}`,
        { fontFamily: 'monospace', fontSize: '12px', color: '#888' }
      ).setOrigin(0.5);
      this.makeBtn(CANVAS_W / 2 + 210, 232, '✕', () => {
        clearAutosave();
        this.scene.restart({ snapshot: this.snapshot });
      }, 14);
    } else {
      this.add.text(CANVAS_W / 2, 232, 'AUTOSAVE — niciun save automat',
        { fontFamily: 'monospace', fontSize: '12px', color: '#444' }
      ).setOrigin(0.5);
    }

    for (const slot of [1, 2, 3] as const) {
      const existing = loadSlot(slot);
      const label = existing
        ? `SLOT ${slot} — ${getWorldTheme(existing.data.world)} (${existing.data.world}-${existing.data.stage}) · ${relativeTime(existing.savedAt)}`
        : `SLOT ${slot} — gol`;
      const y = 278 + (slot - 1) * 58;
      this.makeBtn(CANVAS_W / 2 - 130, y, label, () => {
        if (this.pendingSlot !== null) return;
        if (existing) {
          this.promptOverwrite(slot);
        } else {
          saveSlot(slot, this.snapshot);
          this.flashMessage(`Salvat în slot ${slot} ✓`);
        }
      });
      if (existing) {
        this.makeBtn(CANVAS_W / 2 + 210, y, '✕', () => {
          if (this.pendingSlot !== null) return;
          deleteSlot(slot);
          this.scene.restart({ snapshot: this.snapshot });
        }, 18);
      }
    }

    this.makeBtn(CANVAS_W / 2, CANVAS_H - 90, '⌂ MENIU PRINCIPAL', () => {
      if (this.pendingSlot !== null) return;
      this.goMenu();
    });

    const kb = this.input.keyboard!;
    kb.on('keydown-ESC', () => {
      if (this.pendingSlot !== null) { this.cancelOverwrite(); return; }
      this.resume();
    });
    kb.on('keydown-M', () => {
      if (this.pendingSlot !== null) return;
      this.goMenu();
    });
    kb.on('keydown-ONE',   () => { if (this.pendingSlot !== null) return; this.quickSave(1); });
    kb.on('keydown-TWO',   () => { if (this.pendingSlot !== null) return; this.quickSave(2); });
    kb.on('keydown-THREE', () => { if (this.pendingSlot !== null) return; this.quickSave(3); });

    this.add.text(CANVAS_W / 2, CANVAS_H - 34,
      'Atingere 1/2/3 pentru save rapid • ESC pentru continuare • M pentru meniu',
      { fontFamily: 'monospace', fontSize: '11px', color: '#888' }
    ).setOrigin(0.5);

    resumeBtn.setColor('#ffd166');
  }

  private quickSave(slot: 1 | 2 | 3) {
    const existing = loadSlot(slot);
    if (existing) {
      this.promptOverwrite(slot);
    } else {
      saveSlot(slot, this.snapshot);
      this.flashMessage(`Salvat în slot ${slot} ✓`);
    }
  }

  private promptOverwrite(slot: 1 | 2 | 3) {
    this.pendingSlot = slot;
    const bg = this.add.rectangle(CANVAS_W / 2, CANVAS_H / 2, 420, 130, 0x0a0a1a, 0.96)
      .setStrokeStyle(2, 0x862d91).setOrigin(0.5);
    const msg = this.add.text(CANVAS_W / 2, CANVAS_H / 2 - 28,
      `Suprascrie Slot ${slot}?`, {
        fontFamily: 'monospace', fontSize: '22px', color: '#ffd166',
      }).setOrigin(0.5);
    const yesBtn = this.makeBtn(CANVAS_W / 2 - 90, CANVAS_H / 2 + 22, '[Y] Confirmă', () => this.confirmOverwrite(slot));
    const noBtn  = this.makeBtn(CANVAS_W / 2 + 90, CANVAS_H / 2 + 22, '[N] Anulează', () => this.cancelOverwrite());
    this.confirmGroup = [bg, msg, yesBtn, noBtn];

    const kb = this.input.keyboard!;
    const onY   = () => this.confirmOverwrite(slot);
    const onN   = () => this.cancelOverwrite();
    // Store handlers to remove them cleanly
    (this as unknown as Record<string, unknown>)['_confirmY'] = onY;
    (this as unknown as Record<string, unknown>)['_confirmN'] = onN;
    kb.once('keydown-Y', onY);
    kb.once('keydown-N', onN);
    kb.once('keydown-ENTER', onY);
  }

  private confirmOverwrite(slot: 1 | 2 | 3) {
    if (this.pendingSlot === null) return;
    this.clearConfirmGroup();
    saveSlot(slot, this.snapshot);
    this.flashMessage(`Salvat în slot ${slot} ✓`);
    this.pendingSlot = null;
  }

  private cancelOverwrite() {
    if (this.pendingSlot === null) return;
    this.clearConfirmGroup();
    this.pendingSlot = null;
    this.input.keyboard!.removeAllListeners('keydown-Y');
    this.input.keyboard!.removeAllListeners('keydown-N');
    this.input.keyboard!.removeAllListeners('keydown-ENTER');
  }

  private clearConfirmGroup() {
    this.confirmGroup.forEach(o => (o as Phaser.GameObjects.GameObject).destroy());
    this.confirmGroup = [];
  }

  private makeBtn(x: number, y: number, label: string, onClick: () => void, size = 18): Phaser.GameObjects.Text {
    const t = this.add.text(x, y, label, {
      fontFamily: 'monospace', fontSize: `${size}px`, color: '#fff',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    t.on('pointerover', () => t.setColor('#ffd166'));
    t.on('pointerout', () => t.setColor('#fff'));
    t.on('pointerdown', onClick);
    return t;
  }

  private flashMessage(msg: string) {
    const t = this.add.text(CANVAS_W / 2, CANVAS_H - 140, msg, {
      fontFamily: 'monospace', fontSize: '18px', color: '#06d6a0',
    }).setOrigin(0.5);
    this.tweens.add({ targets: t, alpha: { from: 1, to: 0 }, duration: 1200, onComplete: () => t.destroy() });
  }

  private resume() {
    this.scene.stop();
    this.scene.resume('GameScene');
  }

  private goMenu() {
    this.scene.stop();
    this.scene.stop('GameScene');
    this.scene.start('MenuScene');
  }
}
